import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, input, model, signal, viewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { ArgDef, ArgEntry, VarDef } from '../../models';
import { composePersonalizationFields, parseDeclaredFields } from '../../utils/personalization-fields';
import { PERSONALIZATION_FIELDS_KEY } from '../../utils/arg-key';


/**
 * Prefix marking a field that holds an image rather than text.
 *
 * The player resolves an `IMAGE` content item's `dataVariable` straight into
 * `new Image().src` with no path prefixing (`data.service.ts` → `prepareContent`),
 * so a `data:` URI works as the value. What it can't tell us is *which* fields
 * are images: `dataType: 'IMAGE'` lives on the scene content, while `VarDef`
 * only carries string/number/boolean.
 *
 * Rather than plumb `dataType` through discovery and three copies of `VarDef`,
 * this keys off the name — which matches the data exactly. Every
 * `dataType: 'IMAGE'` variable across the existing projects is prefixed this
 * way (`IMAGE_PHOTO`, `IMAGE_PLAYER_PHOTO`, `IMAGE_LINKEDIN_SCREENSHOT`) and no
 * text variable is. Matching on "PHOTO" instead would miss the screenshot one.
 */
const IMAGE_FIELD_PREFIX = 'IMAGE_';

/** Longest edge, in px, an uploaded image is scaled down to before encoding. */
const MAX_IMAGE_EDGE = 1280;

/** JPEG quality used when re-encoding a downscaled photo. */
const JPEG_QUALITY = 0.85;

/** One input the dialog renders, resolved from the project's arg/var definitions. */
interface PersonalizationField {
	key: string;
	/** Human-facing label; defaults to `key` when the config declares none. */
	label: string;
	/** Empty → free-text input; non-empty → `<select>`. Always empty for images. */
	options: string[];
	/** Render a file picker and store the picked image as a `data:` URI. */
	isImage: boolean;
	/**
	 * Template composing this field's value from the ones the publisher fills,
	 * via `{KEY}` interpolation — e.g. `{FIRST_NAME} {LAST_NAME}`. When set, the
	 * field is never entered by hand; it's computed on submit (DEV-673).
	 */
	derivedFrom?: string;
	/** Key of another field; this one is computed as whether that one is filled (DEV-1064). */
	trueIfFilled?: string;
	/** Not rendered as an input. Implied for computed fields. */
	hidden: boolean;
}

@Component({
	selector: 'app-personalized-link-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, ReactiveFormsModule, Button, InputText],
	templateUrl: './personalized-link-dialog.html',
	styleUrl: './personalized-link-dialog.scss',
})
export class PersonalizedLinkDialogComponent {
	visible = model(false);
	slug = input<string>('');
	argDefs = input<ReadonlyArray<ArgDef>>([]);
	varDefs = input<ReadonlyArray<VarDef>>([]);
	/** Saved var values — the field list itself lives in one of them. */
	vars = input<ReadonlyArray<ArgEntry>>([]);
	/** Project `namesMap`, the only source of options for `variant` args. */
	namesMap = input<Record<string, string[]>>({});

	private http = inject(HttpClient);
	private messageService = inject(MessageService);
	private destroyRef = inject(DestroyRef);

	private cameraVideo = viewChild<ElementRef<HTMLVideoElement>>('cameraVideo');

	/** Field whose camera is currently open, or null. Only one at a time. */
	protected capturingField = signal<string | null>(null);
	private cameraStream: MediaStream | null = null;

	protected submitting = signal(false);
	protected error = signal<string | null>(null);
	/** Set once a link exists — the dialog switches from form to result. */
	protected createdUuid = signal<string | null>(null);

	protected createdUrl = computed(() => {
		const uuid = this.createdUuid();
		return uuid ? `${environment.personalizedLinkBaseUrl}/${uuid}` : '';
	});

	/**
	 * The fields to render, parsed from the reserved `PERSONALIZATION_FIELDS` var.
	 *
	 * Two accepted shapes:
	 *
	 *   `JMENO,GENDER,IMAGE_PHOTO`              — plain list, type inferred
	 *   `{"JMENO":"text","IMAGE_PHOTO":"image"}` — JSON, type declared
	 *
	 * The list form is quicker to type into the config panel; the JSON form says
	 * outright which fields are images instead of relying on the `IMAGE_` naming
	 * rule. A malformed JSON value falls back to the list parser rather than
	 * rendering nothing, so a stray brace can't silently empty the dialog.
	 *
	 * The JSON form also accepts an object per field to declare a friendly
	 * `label` and a `derivedFrom` template — a field composed from the ones the
	 * publisher fills rather than entered directly (DEV-673):
	 *
	 *   `{"FIRST_NAME":"variant","LAST_NAME":"text",
	 *     "TEXT_NAME":{"derivedFrom":"{FIRST_NAME} {LAST_NAME}"}}`
	 *
	 * lets a publisher pick the audio name once and get the on-screen `TEXT_NAME`
	 * filled for free instead of re-typing it.
	 *
	 * Whatever the shape, a field's *control* is still resolved from the
	 * project's definitions: a `variant` arg (e.g. GENDER, which branches the
	 * video) becomes a select over `namesMap`, an arg with `options` becomes a
	 * select over those, a boolean becomes true/false, everything else is text.
	 */
	protected fields = computed<PersonalizationField[]>(() => {
		const raw = (this.vars().find(v => v.key === PERSONALIZATION_FIELDS_KEY)?.value ?? '').trim();
		const declared = parseDeclaredFields(raw);

		const seen = new Set<string>();
		const out: PersonalizationField[] = [];
		for (const { key, type, label, derivedFrom, trueIfFilled, hidden } of declared) {
			if (seen.has(key) || key === PERSONALIZATION_FIELDS_KEY) continue;
			seen.add(key);
			// An explicit `"image"` wins; otherwise fall back to the naming rule.
			const isImage = type === 'image' || (type === undefined && key.startsWith(IMAGE_FIELD_PREFIX));
			out.push({
				key,
				label: label ?? key,
				options: isImage ? [] : this.optionsFor(key),
				isImage,
				derivedFrom,
				trueIfFilled,
				hidden: hidden ?? false,
			});
		}
		return out;
	});

	/** Fields the publisher actually fills in — derived/hidden ones are composed on submit. */
	protected visibleFields = computed(() => this.fields().filter(f => !f.hidden));

	/** Preview data URIs for image fields, keyed by field. Drives the thumbnail + size hint. */
	protected imagePreviews = signal<Record<string, string>>({});

	protected form = signal(new FormGroup<Record<string, FormControl<string>>>({}));

	constructor() {
		// Rebuild the form whenever the field list changes — the dialog is created
		// with the editor, long before the config that defines its inputs loads.
		effect(() => {
			const controls: Record<string, FormControl<string>> = {};
			for (const field of this.visibleFields()) {
				controls[field.key] = new FormControl('', { nonNullable: true, validators: [Validators.required] });
			}
			this.form.set(new FormGroup(controls));
		});

		// Reopening must not show the previous recipient's link — or their photo.
		effect(() => {
			if (this.visible()) {
				this.createdUuid.set(null);
				this.error.set(null);
				this.imagePreviews.set({});
			} else {
				// Closing the dialog while the camera is live would leave the
				// webcam indicator on with no visible UI explaining why.
				this.stopCamera();
			}
		});

		// The <video> only exists while a camera is open, so the stream can't be
		// attached at getUserMedia time — wait for the element to render.
		effect(() => {
			const el = this.cameraVideo()?.nativeElement;
			if (el && this.cameraStream && el.srcObject !== this.cameraStream) {
				el.srcObject = this.cameraStream;
			}
		});

		this.destroyRef.onDestroy(() => this.stopCamera());
	}

	protected async startCamera(key: string): Promise<void> {
		this.stopCamera();
		try {
			this.cameraStream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
				audio: false,
			});
			this.capturingField.set(key);
		} catch {
			// Denied permission, no camera, or an insecure context — getUserMedia
			// is unavailable outside HTTPS and localhost. Uploading still works.
			this.messageService.add({
				severity: 'error',
				summary: 'Camera unavailable',
				detail: 'Could not access a camera. You can still upload a file.',
				life: 4000,
			});
		}
	}

	protected capturePhoto(key: string): void {
		const video = this.cameraVideo()?.nativeElement;
		if (!video || !video.videoWidth) return;

		const dataUri = scaleAndEncode(video, video.videoWidth, video.videoHeight);
		this.form().get(key)?.setValue(dataUri);
		this.imagePreviews.update(prev => ({ ...prev, [key]: dataUri }));
		this.stopCamera();
	}

	protected stopCamera(): void {
		this.cameraStream?.getTracks().forEach(track => track.stop());
		this.cameraStream = null;
		this.capturingField.set(null);
	}

	/**
	 * Read a picked image, scale it down, and store it as a `data:` URI in the
	 * form control.
	 *
	 * Downscaling is not cosmetic. The value ends up inside the personalized-link
	 * document in Mongo, base64 inflates bytes by ~33 %, and the document is read
	 * on every view of the link — a straight 4 MB phone photo would be ~5.3 MB of
	 * document against a 16 MB cap. Re-encoded at 1280 px it lands around 200 kB.
	 *
	 * PNG input stays PNG: `IMAGE_LINKEDIN_SCREENSHOT` is a screenshot, and JPEG
	 * would smear its text.
	 */
	protected async onImagePicked(key: string, event: Event): Promise<void> {
		const file = (event.target as HTMLInputElement).files?.[0];
		if (!file) return;

		try {
			const dataUri = await downscaleToDataUri(file);
			this.form().get(key)?.setValue(dataUri);
			this.imagePreviews.update(prev => ({ ...prev, [key]: dataUri }));
		} catch {
			this.messageService.add({
				severity: 'error',
				summary: 'Could not read image',
				detail: `${file.name} could not be decoded`,
				life: 4000,
			});
		}
	}

	protected clearImage(key: string): void {
		this.form().get(key)?.setValue('');
		this.imagePreviews.update(prev => {
			const next = { ...prev };
			delete next[key];
			return next;
		});
	}

	/** Click the hidden file input belonging to a field. */
	protected pickFile(key: string): void {
		document.getElementById(`plink-file-${key}`)?.click();
	}

	/** Approximate decoded size of a data URI, for the "~180 kB" hint. */
	protected previewSizeKb(key: string): number {
		const uri = this.imagePreviews()[key] ?? '';
		const base64 = uri.slice(uri.indexOf(',') + 1);
		return Math.round((base64.length * 3) / 4 / 1024);
	}

	private optionsFor(key: string): string[] {
		const argDef = this.argDefs().find(d => d.key === key);
		if (argDef) {
			if (argDef.type === 'boolean') return ['true', 'false'];
			if (argDef.type === 'variant') {
				return argDef.variantNamesMapKey ? (this.namesMap()[argDef.variantNamesMapKey] ?? []) : [];
			}
			return argDef.options ?? [];
		}
		const varDef = this.varDefs().find(d => d.key === key);
		if (varDef?.type === 'boolean') return ['true', 'false'];
		return [];
	}

	protected submit(): void {
		const form = this.form();
		if (form.invalid || this.submitting()) {
			form.markAllAsTouched();
			return;
		}

		this.submitting.set(true);
		this.error.set(null);

		this.http
			.post<{ uuid: string }>(
				`${environment.apiUrl}/api/projects/${this.slug()}/personalized-links`,
				{ fields: this.composeFields(form.getRawValue()) },
			)
			.subscribe({
				next: (res) => {
					this.submitting.set(false);
					this.createdUuid.set(res.uuid);
				},
				error: () => {
					this.submitting.set(false);
					this.error.set('Failed to create the personalized link. Please try again.');
				},
			});
	}

	/** Add the computed fields (`derivedFrom`, `trueIfFilled`) to what the publisher entered. */
	private composeFields(entered: Record<string, string>): Record<string, string> {
		return composePersonalizationFields(this.fields(), entered);
	}

	protected async copyUrl(): Promise<void> {
		try {
			await navigator.clipboard.writeText(this.createdUrl());
			this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Link copied to clipboard', life: 2000 });
		} catch {
			this.messageService.add({ severity: 'error', summary: 'Copy failed', detail: 'Could not access the clipboard', life: 3000 });
		}
	}

	protected createAnother(): void {
		this.createdUuid.set(null);
		this.form().reset();
	}

	protected close(): void {
		this.visible.set(false);
	}
}

/**
 * Draw a source (decoded file or live video frame) scaled so its longest edge
 * is at most `MAX_IMAGE_EDGE`, and return it as a `data:` URI. Sources already
 * within the limit are re-encoded at their own size rather than upscaled.
 */
function scaleAndEncode(source: CanvasImageSource, srcWidth: number, srcHeight: number): string {
	const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(srcWidth, srcHeight));
	const canvas = document.createElement('canvas');
	canvas.width = Math.round(srcWidth * scale);
	canvas.height = Math.round(srcHeight * scale);

	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('no 2d context');
	ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

	// Encode both ways and keep whichever is smaller, rather than deciding from
	// the input's MIME type. Keeping PNG in because PNG came in reads sensibly
	// until someone uploads a photograph saved as PNG — measured at 1516 kB
	// where JPEG gave 231 kB for the same image. Flat graphics and screenshots
	// still come out smaller as PNG, so comparing the results picks the right
	// format without having to detect which kind of content it is.
	const png = canvas.toDataURL('image/png');
	const jpeg = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
	return png.length <= jpeg.length ? png : jpeg;
}

/** Decode an image file and hand it to `scaleAndEncode`. */
async function downscaleToDataUri(file: File): Promise<string> {
	const objectUrl = URL.createObjectURL(file);
	try {
		const image = await new Promise<HTMLImageElement>((resolve, reject) => {
			const img = new Image();
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error('decode failed'));
			img.src = objectUrl;
		});
		return scaleAndEncode(image, image.width, image.height);
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}
